/**
 * This file contains utilities to control our Community Widget, powered by Insided
 * The only discoverable documentation lives here:
 * https://community.insided.com/embeddable-widgets-106/the-embeddable-widgets-javascript-api-how-to-use-it-1403
 *
 * Therefore, there is some improversation and hacks we use to get the behavior we want.
 */

import {createElem} from 'common/util/dom';
import {isTestEnv} from 'common/report/util/env';

import {injectCssIntoIframeWhenAttached, moveGlobalActionBar} from './util';
import css from './insided.styl?inline';

type CommunityWidget = any;

enum CommunityWidgetEvent {
  Open = `SLIDER_OPEN`,
  Close = `SLIDER_CLOSE`,
}

enum CommunityWidgetTrackingEvent {
  ClickedTopic = `Clicked Topic`,
  RatedTopic = `Rated Topic`,
  ViewTopicOnCommunity = `View Topic on Community`,
  FailToInject = `Failed to inject css into iframe`,
}

// List of Insided events we want to track mapped to Mixpanel events
const TRACKING_MAP = {
  'insided:conversational:clicked': CommunityWidgetTrackingEvent.ClickedTopic,
  'insided:conversational:topic-rated': CommunityWidgetTrackingEvent.RatedTopic,
  'insided:conversational-widget:view-topic-on-community-clicked': CommunityWidgetTrackingEvent.ViewTopicOnCommunity,
};

const INSIDED_SCRIPT_SRC = `https://embeddable-widgets-usw2.insided.com/mixpanel-en.insided-conversational.js`;
const IFRAME_SEL = `iframe.insided-conversational-slider`;

export async function activateCommunityWidget(): Promise<void> {
  const communityWidget = await loadCommunityWidget();
  if (!communityWidget) {
    return null;
  }

  moveGlobalActionBar({isWidgetPresent: true});

  return communityWidget.$store.dispatch(CommunityWidgetEvent.Open, {});
}

export function hideCommunityWidget(): Promise<void> {
  return window.inSided?.conversational?.$store?.dispatch(CommunityWidgetEvent.Close, {});
}

// Only update if already embedded
export function updateCommunityWidgetFromUrl() {
  window.inSided?.conversational?.reload();
}

function embedCommunityWidget() {
  if (!document.head.querySelector(`script[src='${INSIDED_SCRIPT_SRC}']`)) {
    document.head.appendChild(
      createElem(`script`, {
        src: INSIDED_SCRIPT_SRC,
        async: true,
        defer: true,
      }),
    );
  }
}

let _communityWidgetPromise: Promise<CommunityWidget>;
async function loadCommunityWidget() {
  if (isTestEnv()) {
    return null;
  }

  /* eslint-disable-next-line @typescript-eslint/no-misused-promises */
  if (!_communityWidgetPromise) {
    _communityWidgetPromise = new Promise((resolve) => {
      embedCommunityWidget();

      /* eslint-disable-next-line @typescript-eslint/no-misused-promises */
      window.addEventListener(`insided:conversational:loaded`, function onLoaded() {
        const communityWidget = window.inSided.conversational;

        // Register tracking handlers
        for (const [insidedEvent, mpEvent] of Object.entries(TRACKING_MAP)) {
          communityWidget.subscribe(insidedEvent, (eventObject) => {
            /**
             * The Insided widget API is painfully vague and does not cover event payloads.
             * These tracking properties are found from inspecting the console, hence the heavy safeguarding.
             */
            mixpanel.track(`[Community Widget] ${mpEvent}`, {
              '[Community] Author Name': eventObject?.topic?.author?.name,
              '[Community] Author ID': eventObject?.topic?.author?.id,
              '[Community] Topic ID': eventObject?.topic?.id,
              '[Community] Topic Title': eventObject?.topic?.title,
              '[Community] Topic Type': eventObject?.topic?.type,
              '[Community] Rating': eventObject?.rating,
              '[Community] Views': eventObject?.topic?.metadata?.views,
            });
          });
        }

        injectCssIntoIframeWhenAttached(IFRAME_SEL, css).catch((error) => {
          mixpanel.track(`[Community Widget] ${CommunityWidgetTrackingEvent.FailToInject}`, {error});
        });

        communityWidget.subscribe(`insided:conversational-widget:closed`, () => {
          moveGlobalActionBar({isWidgetPresent: false});
        });

        /**
         * This hack was reccommended directly from the Insided team.
         * They currently have an issue where right after the load event is fired,
         * if you try to open the widget in the callback it opens and closes immediately.
         *
         * Therefore we set a close handler that reopens the widget and only fires once.
         * I have raised concerns that when they actually do fix the issue, it would make the widget
         * reopen when the user closes the first time. They have assured me that they will coordinate with us
         * when the fix is planned, and try their best to come up with a solution that retains compatibility with this hack.
         * - Jakub
         */
        const hackOpen = () => {
          moveGlobalActionBar({isWidgetPresent: true});
          communityWidget.$store.dispatch(CommunityWidgetEvent.Open, {});
          communityWidget.unsubscribe(`insided:conversational-widget:closed`, hackOpen);
        };
        communityWidget.subscribe(`insided:conversational-widget:closed`, hackOpen);

        communityWidget.$store.dispatch(CommunityWidgetEvent.Open, {});
        resolve(communityWidget);

        /* eslint-disable-next-line @typescript-eslint/no-misused-promises */
        window.removeEventListener(`insided:conversational:loaded`, onLoaded);
      });
    });
  }

  return _communityWidgetPromise;
}
